import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.less';
import { Checkbox, Form, Typography } from 'antd';
import ModalContainer from 'components/Common/ModalContainer';
import { DeleteOutlined } from '@ant-design/icons';
import StyledButton from 'components/Common/Buttons/StyledButton';
import { FORM_INPUT_TYPES } from 'utils/constants';
import FormInput from 'components/Common/Input/FormInput';
import { ValidationRule } from 'constants/validationRule';
import NotificationBadge from 'components/NotificationBadge/NotificationBadge';
import { useShareLinksQuota } from '../../hooks/useShareLinksQuata';

const { Text, Title } = Typography;

const initialValues = { users: [{ fullName: '', email: '' }] };

const ShareReferralLinkModal = ({
  isOpen,
  isLoading,
  isReferralLinkLoading,
  handleClose,
  sendInvites,
  onCopyLink,
}) => {
  const { quota = {} } = useShareLinksQuota();
  const bottomOfScrollableAreaRef = useRef(null);

  const [form] = Form.useForm();

  const isInvitesLimitExceeded = quota.limit === quota.used;
  const invitesLeft = quota.limit - quota.used;

  const handleSubmit = ({ users }) => {
    const mappedInvites = users.map(({ email, fullName }) => ({
      clientEmail: email,
      clientFullName: fullName,
    }));

    sendInvites(mappedInvites);
  };

  useEffect(
    () => () => {
      form.resetFields();
    },
    [form, isOpen],
  );

  const scrollBottom = () => {
    setTimeout(() => {
      bottomOfScrollableAreaRef?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 0);
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onCancel={handleClose}
      title={' '}
      footerControls={<></>}
      destroyOnClose
      className={styles.wrapper}
    >
      <Title className={styles.title}>Share your referral link</Title>
      <Text className={styles.subtitle}>
        You can copy your referral link or send an email invite
      </Text>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.List name="users">
          {(fields, { add, remove }) => {
            const isLimitExceeded = fields.length >= invitesLeft;

            return (
              <>
                {!isInvitesLimitExceeded && (
                  <div className={styles.rows}>
                    {fields.map(({ key, name, ...rest }, index) => {
                      const isMultiple = fields.length > 1;

                      return (
                        <div key={key} className={styles.row}>
                          {isMultiple && (
                            <div className={styles.orderTracker}>
                              #{index + 1}
                            </div>
                          )}

                          <Form.Item
                            {...rest}
                            name={[name, 'fullName']}
                            className={styles.inputWrapper}
                          >
                            <FormInput
                              type={FORM_INPUT_TYPES.TEXT}
                              placeholder="Enter your client's full name"
                              label="Client's Full Name"
                              name={[name, 'fullName']}
                              customRulesOnly
                              rules={[ValidationRule.STRING_REQUIRED]}
                            />
                          </Form.Item>
                          <Form.Item
                            {...rest}
                            name={[name, 'email']}
                            className={styles.inputWrapper}
                          >
                            <FormInput
                              type={FORM_INPUT_TYPES.EMAIL}
                              placeholder="Enter your client's full name"
                              label="Client's Email"
                              name={[name, 'email']}
                            />
                          </Form.Item>
                          {isMultiple && (
                            <DeleteOutlined
                              onClick={() => remove(name)}
                              className={styles.deleteIcon}
                            />
                          )}
                        </div>
                      );
                    })}
                    <div ref={bottomOfScrollableAreaRef} />
                  </div>
                )}

                {isLimitExceeded ? (
                  <div className={styles.notificationWrapper}>
                    <NotificationBadge
                      text={`You can only send a maximum of ${quota.limit} invites in ${quota.periodDays} days. Please use the "Copy Link" function if you need to invite more clients.`}
                    />
                  </div>
                ) : (
                  <StyledButton
                    type="text"
                    onClick={() => {
                      add();
                      scrollBottom();
                    }}
                    text="+ Add another invite"
                    className={styles.addButton}
                  />
                )}

                <Form.Item
                  name="checked"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject('You need to agree'),
                    },
                  ]}
                >
                  {!isInvitesLimitExceeded && (
                    <Checkbox className={styles.checkbox}>
                      <Text className={styles.checkboxLabel}>
                        I confirm that my clients have agreed to be emailed by
                        Fusion Markets Partners and reside outside of Australia.
                      </Text>
                    </Checkbox>
                  )}
                </Form.Item>
                <div className={styles.footer}>
                  <StyledButton
                    type="outline"
                    icon="shareLink"
                    onClick={onCopyLink}
                    text="Copy Link"
                    disabled={isReferralLinkLoading}
                    loading={isReferralLinkLoading}
                    className={styles.shareLink}
                  />

                  <div className={styles.buttonsSubWrapper}>
                    <StyledButton
                      type="text"
                      onClick={handleClose}
                      text="Cancel"
                    />
                    <StyledButton
                      type="primary"
                      htmlType="submit"
                      className={styles.sendButton}
                      text="Send invite"
                      disabled={isInvitesLimitExceeded}
                      loading={isLoading}
                    />
                  </div>
                </div>
              </>
            );
          }}
        </Form.List>
      </Form>
    </ModalContainer>
  );
};

ShareReferralLinkModal.propTypes = {
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReferralLinkLoading: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  sendInvites: PropTypes.func.isRequired,
  onCopyLink: PropTypes.func.isRequired,
};

export default ShareReferralLinkModal;
